// Componente de tabla detallada para pedidos
export const DetailedOrdersTable = ({
    orders,
    showHandoffCheckbox = false,
    selectedOrders = [],
    onToggleOrder = null
}) => {
    if (!orders || orders.length === 0) {
        return (
            <div className="text-center py-8 text-gray-500">
                No hay pedidos para mostrar
            </div>
        );
    }

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('es-CO', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    // Determine payment status based on paid_amount vs total amount
    const getPaymentStatus = (order) => {
        const total = (parseFloat(order.total_amount) || 0) + (parseFloat(order.int_amount) || 0);
        const paid = parseFloat(order.paid_amount) || 0;

        // If fully paid
        if (paid >= total && total > 0) {
            return {
                label: 'Pagado',
                color: 'bg-green-100 text-green-800',
                method: order.last_payment_method || 'Completo'
            };
        }

        // If partially paid
        if (paid > 0 && paid < total) {
            const paymentMethodLabel = getPaymentMethodLabel(order.last_payment_method);
            return {
                label: 'Pago parcial',
                color: 'bg-yellow-100 text-yellow-800',
                method: paymentMethodLabel
            };
        }

        // No payment yet - show original payment method
        return {
            label: getPaymentMethodLabel(order.payment_method),
            color: getPaymentMethodColor(order.payment_method),
            method: null
        };
    };

    const getPaymentMethodLabel = (method) => {
        if (!method) return '-';
        const paymentMethods = {
            'efectivo': 'Efectivo',
            'transferencia': 'Transferencia',
            'contraentrega': 'Sin pago',
            'datáfono': 'Datáfono',
            'datafono': 'Datáfono',
            'credito': 'Crédito',
            'recoge_bodega': 'Recoge Bodega',
            'consignacion': 'Consignación',
            'pse': 'PSE'
        };
        return paymentMethods[method?.toLowerCase()] || method;
    };

    const getPaymentMethodColor = (method) => {
        const colors = {
            'efectivo': 'bg-green-100 text-green-800',
            'transferencia': 'bg-blue-100 text-blue-800',
            'contraentrega': 'bg-orange-100 text-orange-800',
            'datáfono': 'bg-purple-100 text-purple-800',
            'datafono': 'bg-purple-100 text-purple-800',
            'credito': 'bg-yellow-100 text-yellow-800',
            'recoge_bodega': 'bg-gray-100 text-gray-800',
            'consignacion': 'bg-blue-100 text-blue-800',
            'pse': 'bg-indigo-100 text-indigo-800'
        };
        return colors[method?.toLowerCase()] || 'bg-gray-100 text-gray-800';
    };

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        {showHandoffCheckbox && (
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                                ✓
                            </th>
                        )}
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            # Pedido
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            Fecha
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            Cliente
                        </th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                            INT
                        </th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                            Días
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                            Valor Siigo
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                            Valor INT
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                            Total
                        </th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                            Estado Pago
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                            Pagado
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                            Saldo
                        </th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {orders.map((order) => {
                        const siigoAmount = parseFloat(order.total_amount) || 0;
                        const intAmount = parseFloat(order.int_amount) || 0;
                        const paidAmount = parseFloat(order.paid_amount) || 0;
                        const totalAmount = siigoAmount + intAmount;
                        const balanceDue = totalAmount - paidAmount;
                        const paymentStatus = getPaymentStatus(order);

                        return (
                            <tr key={order.id} className="hover:bg-gray-50">
                                {showHandoffCheckbox && (
                                    <td className="px-3 py-2 whitespace-nowrap text-center">
                                        <input
                                            type="checkbox"
                                            checked={selectedOrders.includes(order.id)}
                                            onChange={() => onToggleOrder && onToggleOrder(order.id)}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                    </td>
                                )}
                                <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-blue-600">
                                    {order.order_number}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">
                                    {formatDate(order.created_at)}
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-900">
                                    {order.customer_name}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-center text-sm">
                                    {order.int_order_number ? (
                                        <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                                            {order.int_order_number}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-center text-sm text-gray-600">
                                    {order.days_since_created || 0}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                    {formatCurrency(siigoAmount)}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-medium text-purple-600">
                                    {intAmount > 0 ? formatCurrency(intAmount) : '-'}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-bold text-gray-900">
                                    {formatCurrency(totalAmount)}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-center text-xs">
                                    <div className="flex flex-col items-center gap-1">
                                        <span className={`px-2 py-1 rounded ${paymentStatus.color}`}>
                                            {paymentStatus.label}
                                        </span>
                                        {paymentStatus.method && (
                                            <span className="text-xs text-gray-500">
                                                ({paymentStatus.method})
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-medium text-green-600">
                                    {paidAmount > 0 ? formatCurrency(paidAmount) : '-'}
                                </td>
                                <td className={`px-3 py-2 whitespace-nowrap text-sm text-right font-bold ${balanceDue > 0 ? 'text-red-600' : 'text-green-600'
                                    }`}>
                                    {formatCurrency(balanceDue)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot className="bg-gray-50">
                    <tr>
                        <td colSpan={showHandoffCheckbox ? "6" : "5"} className="px-3 py-2 text-right text-sm font-medium text-gray-700">
                            TOTALES:
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-bold text-gray-900">
                            {formatCurrency(orders.reduce((sum, o) => sum + (parseFloat(o.total_amount) || 0), 0))}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-bold text-purple-600">
                            {formatCurrency(orders.reduce((sum, o) => sum + (parseFloat(o.int_amount) || 0), 0))}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-bold text-gray-900">
                            {formatCurrency(orders.reduce((sum, o) => sum + (parseFloat(o.total_amount) || 0) + (parseFloat(o.int_amount) || 0), 0))}
                        </td>
                        <td className="px-3 py-2"></td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-bold text-green-600">
                            {formatCurrency(orders.reduce((sum, o) => sum + (parseFloat(o.paid_amount) || 0), 0))}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-bold text-red-600">
                            {formatCurrency(orders.reduce((sum, o) => {
                                const total = (parseFloat(o.total_amount) || 0) + (parseFloat(o.int_amount) || 0);
                                const paid = parseFloat(o.paid_amount) || 0;
                                return sum + (total - paid);
                            }, 0))}
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};
